import { routes } from "@/routes/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  ACCOUNT_NUMBER,
  AMOUNT,
  AUTO_BILLING,
  BILLING_CATEGORY,
  BILLING_DATE,
  COMMUNITY_ID,
  CURRENCY,
  DESCRIPTION,
  DUE_DATE,
  FREQUENCY,
  INTEREST,
  MONTHLY_BILLING_DATE,
  MONTHLY_POSTING_DATE,
  PAYMENT_TERM,
  POSTING_DATE,
  PROPERTY_CATEGORY_ID,
  PROPERTY_IDS,
  TRANSACTION_TYPE,
  YEAR,
} from "./consts";
import { useNavigate, useParams } from "react-router-dom";
import { addSchema, AddModelFormValues } from "./validations";
import { toast } from "@/components/Toast";
import {
  useAddBillingConfigMutation,
  useGetBillingCategoryDropdown,
  useGetBillingConfigById,
  useGetDocumentTypeDropdown,
  useGetGlMasterDataDropdown,
  useGetPaymentTermDropdown,
  useGetPropertyByPropertyCategory,
  useRunBillingJobMutation,
} from "@/services";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import moment from "moment";

const useAddEdit = () => {
  const { billingId, action } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [keys, setKeys] = useState<any>({
    accountName: Math.random() * 2,
    billingCategory: Math.random() * 2,
    transactionType: Math.random() * 2,
    paymentTerm: Math.random() * 2,
    freq: Math.random() * 2,
    propertyModel: Math.random() * 2,
  });
  const financialYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );
  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const isView = action == "view";

  const form = useForm<AddModelFormValues>({
    resolver: zodResolver(addSchema),
    defaultValues: {
      [COMMUNITY_ID]: communityDetail?.communityId?.toString(),
      [YEAR]: financialYear?.toString(),
      [BILLING_CATEGORY]: ["all"],
      [TRANSACTION_TYPE]: "",
      [AUTO_BILLING]: true,
      [ACCOUNT_NUMBER]: "",
      [FREQUENCY]: "",
      [AMOUNT]: "",
      [CURRENCY]: "USD",
      [INTEREST]: "",
      [BILLING_DATE]: "",
      [DUE_DATE]: "",
      [POSTING_DATE]: "",
      [DESCRIPTION]: "",
      [PROPERTY_CATEGORY_ID]: "",
      [PROPERTY_IDS]: ["all"],
    },
  });

  const requiredFields = getRequiredFields(addSchema);

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.cam}/${routes.activityDashboard}/${routes.billingConfig}`,
        name: <span>Billing Configuration</span>,
      },
      {
        link: "",
        name: (
          <span className="text-primary">
            {isView ? "View" : "Add"} Billing Configuration
          </span>
        ),
      },
    ],
    []
  );

  const onBackClick = () => {
    navigate(
      `/${routes.cam}/${routes.activityDashboard}/${routes.billingConfig}`
    );
  };

  const { data: glData, isLoading: isLoadingGlMaster } =
    useGetGlMasterDataDropdown({
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: billingConfigData, isLoading: isBillingConfigLoading } =
    useGetBillingConfigById({
      communityId: communityDetail?.communityId,
      pricingId: billingId,
      queryParams: {
        enabled: !!(communityDetail?.communityId && billingId),
      },
    });

  const { data: billingCategory, isLoading: isLoadingBillingCategory } =
    useGetBillingCategoryDropdown({
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: paymentTerms, isLoading: isPaymentTermLoading } =
    useGetPaymentTermDropdown({
      communityId: communityDetail?.communityId,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const { data: propertyByModel, isLoading: isPropertyLoading } =
    useGetPropertyByPropertyCategory({
      communityId: communityDetail?.communityId,
      categoryIds: form.watch(PROPERTY_CATEGORY_ID) as any,
      queryParams: { enabled: !!form.watch(PROPERTY_CATEGORY_ID) },
    });

  const { data: documentTypes, isLoading: isDocumentTypeLoading } =
    useGetDocumentTypeDropdown({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  useEffect(() => {
    if (documentTypes) {
      if (!billingId) {
        const documentTypeId = documentTypes
          ?.find((data: any) => {
            return data?.documentType?.toLowerCase() == "ob";
          })
          ?.documentTypeId?.toString();
        if (!documentTypeId) {
          toast.error(
            "The Document Type 'OB' was not found. Kindly add it to proceed."
          );
          onBackClick();
        }
      }
    }
  }, [documentTypes]);

  const { mutate: addInfo, isPending: isAddPending } =
    useAddBillingConfigMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Billing Configuration Added Successfully");
        queryClient.invalidateQueries({
          queryKey: ["billing-config-list"],
        });
        onBackClick();
      },
    });

  const { mutate: runJobInfo, isPending: isJobPending } =
    useRunBillingJobMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Billing Configuration Added Successfully");
        queryClient.invalidateQueries({
          queryKey: ["billing-config-list"],
        });
        onBackClick();
      },
    });

  const onRunJob = () => {
    runJobInfo({
      data: {
        communityId: communityDetail?.communityId,
        pricingId: billingId,
      },
    });
  };

  const onSubmit = (data: AddModelFormValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(data);

    if (_data[FREQUENCY] === "AD_HOC") {
      _data[PAYMENT_TERM] = null;
    }


    if (!_data[BILLING_CATEGORY]) {
      form.setError(BILLING_CATEGORY, { message: "This Field is required" });
      return;
    }

    if (_data[FREQUENCY] == "ANNUAL" && !_data[BILLING_DATE]) {
      form.setError(BILLING_DATE, { message: "This Field is required" });
      return;
    } else if (_data[FREQUENCY] == "MONTHLY" && !_data[MONTHLY_BILLING_DATE]) {
      form.setError(MONTHLY_BILLING_DATE, {
        message: "This Field is required",
      });
      return;
    }

    if (_data[FREQUENCY] == "MONTHLY" && !_data[MONTHLY_POSTING_DATE]) {
      form.setError(MONTHLY_POSTING_DATE, {
        message: "This Field is required",
      });
      return;
    }

    if (_data[FREQUENCY] == "ANNUAL" && !_data[DUE_DATE]) {
      form.setError(DUE_DATE, { message: "This Field is required" });
      return;
    }

    if (_data[FREQUENCY] == "ANNUAL" || _data[FREQUENCY] == "AD_HOC") {
      delete _data[MONTHLY_BILLING_DATE];
      _data[BILLING_DATE] = moment(_data[BILLING_DATE] as any).format(
        "YYYY-MM-DD"
      );
      _data[DUE_DATE] = moment(_data[DUE_DATE] as any).format("YYYY-MM-DD");
    } else if (_data[FREQUENCY] == "MONTHLY") {
      delete _data[BILLING_DATE];
      delete _data[DUE_DATE];
    }

    if (_data[FREQUENCY] == "ANNUAL" && !_data[DUE_DATE]) {
      form.setError(DUE_DATE, { message: "This Field is required" });
      return;
    }

    if ((_data[PROPERTY_IDS] as any)?.includes("all")) {
      _data[PROPERTY_IDS] = (propertyByModel?.data || [])?.map((val: any) =>
        val?.propertyId?.toString()
      );
    }
    if (_data[FREQUENCY] == "ANNUAL" || _data[FREQUENCY] == "AD_HOC") {
          delete _data[MONTHLY_BILLING_DATE];
    
          _data[BILLING_DATE] = moment(_data[BILLING_DATE] as any).format("YYYY-MM-DD");
          _data[DUE_DATE] = moment(_data[DUE_DATE] as any).format("YYYY-MM-DD");
          _data[POSTING_DATE] = moment(_data[POSTING_DATE] as any).format("YYYY-MM-DD");
        }

        if ((_data[BILLING_CATEGORY] as any)?.includes("all")) {
          _data[BILLING_CATEGORY] = (billingCategory?.data || [])?.map((data: any) => data?.id?.toString());
        }

    addInfo({
      data: { ..._data },
    });
  };

  useEffect(() => {
    if (billingConfigData) {
      form.reset({
        [COMMUNITY_ID]: communityDetail?.communityId?.toString(),
        [YEAR]: financialYear?.toString(),
        [BILLING_CATEGORY]:
          billingConfigData?.data?.billingCategories?.map((val: any) =>
            val?.billingCategoryId?.toString()
          ) || ["all"],
        [PROPERTY_CATEGORY_ID]:
          billingConfigData?.data?.billingCategories?.map((val: any) =>
            val?.propertyModelId?.toString()
          ) || [],
        [PAYMENT_TERM]:
          billingConfigData?.data?.[PAYMENT_TERM]?.toString() || "",
        [TRANSACTION_TYPE]:
          billingConfigData?.data?.[TRANSACTION_TYPE]?.toString() || "",
        [AUTO_BILLING]: billingConfigData?.data?.[AUTO_BILLING],
        [ACCOUNT_NUMBER]:
          billingConfigData?.data?.[ACCOUNT_NUMBER]?.toString() || "",
        [FREQUENCY]: billingConfigData?.data?.[FREQUENCY]?.toString() || "",
        [AMOUNT]: billingConfigData?.data?.[AMOUNT]?.toString() || "",
        [CURRENCY]: "USD",
        [INTEREST]: billingConfigData?.data?.[INTEREST]?.toString() || "",
        [BILLING_DATE]: billingConfigData?.data?.[BILLING_DATE]
          ? moment(billingConfigData?.data?.[BILLING_DATE])?.toString()
          : "",
        [DUE_DATE]: billingConfigData?.data?.[DUE_DATE]
          ? moment(billingConfigData?.data?.[DUE_DATE]).toString()
          : "",
        [POSTING_DATE]: billingConfigData?.data?.[POSTING_DATE]
          ? moment(billingConfigData?.data?.[POSTING_DATE]).toString()
          : "",
        [DESCRIPTION]: billingConfigData?.data?.[DESCRIPTION]?.toString() || "",
        [PROPERTY_IDS]: billingConfigData?.data?.[PROPERTY_IDS]?.map(
          (val: any) => val.toString()
        ),
      });
      onBillingCategoryChange(billingConfigData?.data?.billingCategories);
      setKeys({
        accountName: Math.random() * 4,
        billingCategory: Math.random() * 4,
        transactionType: Math.random() * 4,
        paymentTerm: Math.random() * 4,
        freq: Math.random() * 4,
        propertyModel: Math.random() * 4,
      });
    }
  }, [billingConfigData]);

  const isJobExecuted = billingConfigData?.data?.lastBillingDate
    ? Number(moment(billingConfigData?.data?.lastBillingDate).format("MM")) ==
    Number(moment().format("MM"))
    : false;

  useEffect(() => {
    if (paymentTerms) {
      setKeys((prev: any) => ({ ...prev, paymentTerm: Math.random() * 4 }));
    }
  }, [paymentTerms]);

  useEffect(() => {
    if (billingCategory) {
      setKeys((prev: any) => ({ ...prev, billingCategory: Math.random() * 4 }));
    }
  }, [billingCategory]);

  useEffect(() => {
    if (propertyByModel) {
      setKeys((prev: any) => ({ ...prev, propertyModel: Math.random() * 4 }));
    }
  }, [propertyByModel]);

  const onBillingCategoryChange = (value: any) => {
    const selectedCategories = value?.includes("all")
      ? billingCategory?.data
      : value?.map((id: any) => {
          if (typeof id == "object") return id;
          return billingCategory?.data?.find(
            (cat: any) => cat?.id?.toString() == id
          );
        });
    const filterBillingCategory = selectedCategories?.map(
      (category: any) => category?.propertyCategories?.flat()?.map((item: any) => item.categoryId)
    );
    form.setValue(PROPERTY_CATEGORY_ID, filterBillingCategory?.flat()?.join(","));
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    requiredFields,
    onCancel: onBackClick,
    billingCategory,
    isLoadingBillingCategory,
    glData,
    isLoadingGlMaster,
    keys,
    communityDetail,
    paymentTerms,
    isPaymentTermLoading,
    isView,
    isBillingConfigLoading,
    isJobPending,
    billingConfigData,
    isJobExecuted,
    onRunJob,
    propertyByModel,
    isPropertyLoading,
    onBillingCategoryChange,
    isDocumentTypeLoading
  };
};

export default useAddEdit;
